import { Fragment, useId, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties, KeyboardEvent as ReactKeyboardEvent, RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { editAction } from '../model/keys';
import { dropdownPlacement } from '../model/dropdown';
import type { GridOption, GridValue } from '../model/types';
import { GridIcon } from '../../../ui/StandIcon';

/** How an editor finishes: commit a value (or text to parse) and move, or cancel. */
export interface EditorDone {
  commit(value: { text: string } | { value: GridValue }, dRow: number, dCol: number): void;
  cancel(): void;
}

interface BaseProps extends EditorDone {
  label: string;
}

/** Fixed position below an element (dropdowns must escape the grid's scroll container). */
function useDropdownPosition(anchor: RefObject<HTMLElement | null>): CSSProperties {
  const [style, setStyle] = useState<CSSProperties>({ position: 'fixed', visibility: 'hidden' });
  useLayoutEffect(() => {
    const place = () => {
      const r = anchor.current?.getBoundingClientRect();
      if (!r) return;
      setStyle(dropdownPlacement(r, window.innerHeight));
    };
    place();
    // follow the cell when the table (or anything around it) scrolls, instead of staying behind
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [anchor]);
  return style;
}

/** Text / number cell editor: Enter, Shift+Enter, Tab, Shift+Tab commit and move, Escape cancels. */
export function TextEditor({
  initial,
  label,
  numeric,
  commit,
  cancel,
}: BaseProps & { initial: string; numeric?: boolean }) {
  const [draft, setDraft] = useState(initial);
  const done = useRef(false);
  const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    const a = editAction(e);
    if (a.type === 'none') return;
    e.preventDefault();
    e.stopPropagation();
    done.current = true;
    if (a.type === 'cancel') cancel();
    else commit({ text: draft }, a.dRow, a.dCol);
  };
  return (
    <input
      className="dg-editor"
      // eslint-disable-next-line jsx-a11y/no-autofocus -- opened by an explicit edit gesture
      autoFocus
      aria-label={label}
      inputMode={numeric ? 'decimal' : undefined}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={onKeyDown}
      onFocus={(e) => {
        const len = e.currentTarget.value.length;
        e.currentTarget.setSelectionRange(len, len);
      }}
      onBlur={() => {
        if (!done.current) {
          done.current = true;
          commit({ text: draft }, 0, 0);
        }
      }}
    />
  );
}

/** Enum cell editor (native select, keyboard friendly). */
export function SelectEditor({
  initial,
  options,
  label,
  commit,
  cancel,
}: BaseProps & { initial: string; options: readonly GridOption[] }) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(initial);
  const done = useRef(false);
  return (
    <select
      className="dg-editor"
      // eslint-disable-next-line jsx-a11y/no-autofocus -- opened by an explicit edit gesture
      autoFocus
      aria-label={label}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={(e) => {
        const a = editAction(e);
        if (a.type === 'none') return;
        e.preventDefault();
        e.stopPropagation();
        done.current = true;
        if (a.type === 'cancel') cancel();
        else commit({ value: draft }, a.dRow, a.dCol);
      }}
      onBlur={() => {
        if (!done.current) {
          done.current = true;
          commit({ value: draft }, 0, 0);
        }
      }}
    >
      {/* a column with its own empty option ("unassigned") gets no second one (OIL-213) */}
      {!options.some((o) => o.value === '') && <option value="">{t('grid.none')}</option>}
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

/** Free text with suggestions (catalog models, item names): a combobox with a listbox below. */
export function AutocompleteEditor({
  initial,
  label,
  suggest,
  commit,
  cancel,
}: BaseProps & { initial: string; suggest(q: string): readonly GridOption[] }) {
  const [draft, setDraft] = useState(initial);
  const [active, setActive] = useState(-1);
  const [open, setOpen] = useState(true);
  const done = useRef(false);
  const input = useRef<HTMLInputElement>(null);
  const id = useId();
  // the unchanged cell value shows every suggestion (alternatives), typing filters (U68)
  const options = open ? suggest(draft === initial ? '' : draft).slice(0, 16) : [];
  const style = useDropdownPosition(input);
  const finish = (value: string, dRow: number, dCol: number) => {
    done.current = true;
    commit({ text: value }, dRow, dCol);
  };
  const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      e.stopPropagation();
      setOpen(true);
      const n = options.length;
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setActive((i) => {
        const next = i + step;
        return next >= n ? -1 : next < -1 ? n - 1 : next;
      });
      return;
    }
    const a = editAction(e);
    if (a.type === 'none') return;
    e.preventDefault();
    e.stopPropagation();
    if (a.type === 'cancel') {
      if (open && active >= 0) {
        setActive(-1);
        setOpen(false);
        return;
      }
      done.current = true;
      cancel();
      return;
    }
    const picked = active >= 0 ? options[active] : undefined;
    finish(picked ? picked.value : draft, a.dRow, a.dCol);
  };
  return (
    <>
      <input
        ref={input}
        className="dg-editor"
        // eslint-disable-next-line jsx-a11y/no-autofocus -- opened by an explicit edit gesture
        autoFocus
        role="combobox"
        aria-label={label}
        aria-autocomplete="list"
        aria-expanded={options.length > 0}
        aria-controls={`${id}-list`}
        aria-activedescendant={active >= 0 && options[active] ? `${id}-opt-${active}` : undefined}
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
          setActive(-1);
          setOpen(true);
        }}
        onKeyDown={onKeyDown}
        onBlur={() => {
          if (!done.current) finish(draft, 0, 0);
        }}
      />
      {options.length > 0 && (
        <ul
          id={`${id}-list`}
          role="listbox"
          aria-label={label}
          className="dg-dropdown"
          style={style}
        >
          {options.map((o, i) => (
            <Fragment key={`${o.value}-${i}`}>
              {o.group !== undefined && o.group !== options[i - 1]?.group && (
                <li role="presentation" className="dg-dropdown-group">
                  {o.group}
                </li>
              )}
              <li
                id={`${id}-opt-${i}`}
                role="option"
                aria-selected={i === active}
                className={i === active ? 'is-active' : undefined}
                onPointerDown={(e) => {
                  e.preventDefault();
                  finish(o.value, 0, 0);
                }}
              >
                {o.icon !== undefined && <GridIcon icon={o.icon} />}
                <span>{o.label}</span>
                {o.hint && <span className="muted small">{o.hint}</span>}
              </li>
            </Fragment>
          ))}
        </ul>
      )}
    </>
  );
}

/** Multi-select (e.g. mix → outputs): type to filter, ↑/↓ move, Space toggles, Enter commits. */
export function MultiSelectEditor({
  initial,
  options,
  label,
  commit,
  cancel,
}: BaseProps & { initial: readonly string[]; options: readonly GridOption[] }) {
  const { t } = useTranslation();
  const [chosen, setChosen] = useState<string[]>([...initial]);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const done = useRef(false);
  const input = useRef<HTMLInputElement>(null);
  const id = useId();
  const style = useDropdownPosition(input);
  const q = query.trim().toLowerCase();
  const visible = options.filter((o) => q === '' || o.label.toLowerCase().includes(q));
  const toggle = (value: string) =>
    setChosen((c) => (c.includes(value) ? c.filter((v) => v !== value) : [...c, value]));
  const finish = (dRow: number, dCol: number) => {
    done.current = true;
    commit({ value: options.map((o) => o.value).filter((v) => chosen.includes(v)) }, dRow, dCol);
  };
  return (
    <>
      <input
        ref={input}
        className="dg-editor"
        // eslint-disable-next-line jsx-a11y/no-autofocus -- opened by an explicit edit gesture
        autoFocus
        role="combobox"
        aria-label={label}
        aria-expanded="true"
        aria-controls={`${id}-list`}
        aria-activedescendant={visible[active] ? `${id}-opt-${active}` : undefined}
        placeholder={t('grid.filterOptions')}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value.replace(/ /g, ''));
          setActive(0);
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            e.stopPropagation();
            const n = Math.max(visible.length, 1);
            setActive((i) => (i + (e.key === 'ArrowDown' ? 1 : -1) + n) % n);
            return;
          }
          if (e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            const o = visible[active];
            if (o) toggle(o.value);
            return;
          }
          const a = editAction(e);
          if (a.type === 'none') return;
          e.preventDefault();
          e.stopPropagation();
          if (a.type === 'cancel') {
            done.current = true;
            cancel();
          } else finish(a.dRow, a.dCol);
        }}
        onBlur={() => {
          if (!done.current) finish(0, 0);
        }}
      />
      <ul
        id={`${id}-list`}
        role="listbox"
        aria-multiselectable="true"
        aria-label={label}
        className="dg-dropdown"
        style={style}
      >
        {visible.length === 0 && <li className="muted small">{t('grid.noOptions')}</li>}
        {visible.map((o, i) => (
          <li
            key={o.value}
            id={`${id}-opt-${i}`}
            role="option"
            aria-selected={chosen.includes(o.value)}
            className={i === active ? 'is-active' : undefined}
            onPointerDown={(e) => {
              e.preventDefault();
              setActive(i);
              toggle(o.value);
            }}
          >
            <span className="dg-check" aria-hidden="true">
              {chosen.includes(o.value) ? '✓' : ''}
            </span>
            <span>{o.label}</span>
            {o.hint && <span className="muted small">{o.hint}</span>}
          </li>
        ))}
      </ul>
    </>
  );
}
